import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { universitiesAPI, countriesAPI, adminAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ArrowRight, Upload, X, Building2, Award } from 'lucide-react';

// File upload constants
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const AdminUniversities = () => {
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '', name_fa: '', name_en: '', country_id: '', city_fa: '',
    logo_url: '', image_url: '', rank: '', university_type: '',
    teaching_language: '', tuition_range: '', description: '',
    available_fields: '', degrees: '', admission_requirements: '',
    website_url: '', contact_email: '', contact_phone: '', application_deadline: '',
    dormitories: [], scholarships: [], facilities: []
  });

  const [dormInput, setDormInput] = useState({ name: '', address: '', monthly_cost: '', facilities: '' });
  const [scholarshipInput, setScholarshipInput] = useState({ name: '', amount: '', requirements: '', deadline: '' });

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        await fetchData();
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - navigate is stable from useNavigate

  const fetchData = async () => {
    try {
      const [uniRes, countryRes] = await Promise.all([
        universitiesAPI.getAll(),
        countriesAPI.getAll()
      ]);
      setUniversities(uniRes.data);
      setCountries(countryRes.data);
    } catch (error) {
      toast.error('خطا در دریافت اطلاعات');
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`حجم فایل نباید بیشتر از ${MAX_FILE_SIZE_MB} مگابایت باشد`);
      return;
    }
    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setFormData({ ...formData, [field]: imageUrl });
      toast.success('تصویر با موفقیت آپلود شد');
    } catch (error) {
      toast.error('خطا در آپلود تصویر');
    } finally {
      setUploading(false);
    }
  };

  const addDormitory = () => {
    if (!dormInput.name.trim()) {
      toast.error('نام خوابگاه را وارد کنید');
      return;
    }
    const newDorm = {
      name: dormInput.name,
      address: dormInput.address,
      monthly_cost: dormInput.monthly_cost,
      facilities: dormInput.facilities.split(',').map(f => f.trim()).filter(f => f)
    };
    setFormData({ ...formData, dormitories: [...formData.dormitories, newDorm] });
    setDormInput({ name: '', address: '', monthly_cost: '', facilities: '' });
  };

  const removeDormitory = (index) => {
    const updated = formData.dormitories.filter((_, i) => i !== index);
    setFormData({ ...formData, dormitories: updated });
  };

  const addScholarship = () => {
    if (!scholarshipInput.name.trim()) {
      toast.error('نام بورسیه را وارد کنید');
      return;
    }
    const newScholarship = {
      name: scholarshipInput.name,
      amount: scholarshipInput.amount,
      requirements: scholarshipInput.requirements,
      deadline: scholarshipInput.deadline
    };
    setFormData({ ...formData, scholarships: [...formData.scholarships, newScholarship] });
    setScholarshipInput({ name: '', amount: '', requirements: '', deadline: '' });
  };

  const removeScholarship = (index) => {
    const updated = formData.scholarships.filter((_, i) => i !== index);
    setFormData({ ...formData, scholarships: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uniData = {
        ...formData,
        available_fields: formData.available_fields.split(',').map(f => f.trim()).filter(f => f),
        degrees: formData.degrees.split(',').map(d => d.trim()).filter(d => d),
        scholarship_list: formData.scholarships || [],
        facilities: []
      };

      delete uniData.scholarships;

      if (editingUniversity) {
        await universitiesAPI.update(formData.id, uniData);
        toast.success('دانشگاه با موفقیت ویرایش شد');
      } else {
        uniData.id = Date.now().toString();
        await universitiesAPI.create(uniData);
        toast.success('دانشگاه با موفقیت ایجاد شد');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving university:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'خطا در ذخیره دانشگاه';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleEdit = (uni) => {
    setEditingUniversity(uni);
    setFormData({
      ...uni,
      available_fields: uni.available_fields?.join(', ') || '',
      degrees: uni.degrees?.join(', ') || '',
      dormitories: Array.isArray(uni.dormitories) ? uni.dormitories : [],
      scholarships: (() => {
        if (Array.isArray(uni.scholarships)) return uni.scholarships;
        if (Array.isArray(uni.scholarship_list)) return uni.scholarship_list;
        return [];
      })(),
      facilities: Array.isArray(uni.facilities) ? uni.facilities : []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این دانشگاه اطمینان دارید؟')) return;
    try {
      await universitiesAPI.delete(id);
      toast.success('دانشگاه با موفقیت حذف شد');
      fetchData();
    } catch (error) {
      toast.error('خطا در حذف دانشگاه');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '', name_fa: '', name_en: '', country_id: '', city_fa: '',
      logo_url: '', image_url: '', rank: '', university_type: '',
      teaching_language: '', tuition_range: '', description: '',
      available_fields: '', degrees: '', admission_requirements: '',
      website_url: '', contact_email: '', contact_phone: '', application_deadline: '',
      dormitories: [], scholarships: [], facilities: []
    });
    setEditingUniversity(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Facilities handlers
  const [newFacility, setNewFacility] = useState('');
  
  const addFacility = () => {
    if (newFacility.trim()) {
      setFormData({ ...formData, facilities: [...formData.facilities, newFacility.trim()] });
      setNewFacility('');
    }
  };

  const removeFacility = (index) => {
    setFormData({ ...formData, facilities: formData.facilities.filter((_, i) => i !== index) });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#D4AF37] hover:text-[#F3E5AB]"><ArrowRight size={24} /></Link>
            <h1 className="text-3xl font-light text-white">مدیریت دانشگاه‌ها</h1>
          </div>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB]">
            <Plus size={20} />دانشگاه جدید
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {universities.map((uni) => (
            <div key={uni.id} className="glass-effect border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all">
              <div className="h-32 bg-white/5 flex items-center justify-center p-4">
                {uni.logo_url ? <img src={uni.logo_url} alt={uni.name_fa} className="max-h-full max-w-full object-contain" /> : <div className="text-6xl text-zinc-600">🎓</div>}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-medium text-white mb-1">{uni.name_fa}</h3>
                <p className="text-sm text-zinc-400 mb-2">{uni.city_fa} • {uni.rank}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                  {uni.dormitories && uni.dormitories.length > 0 && (
                    <span className="flex items-center gap-1"><Building2 size={12} />{uni.dormitories.length} خوابگاه</span>
                  )}
                  {uni.scholarships && uni.scholarships.length > 0 && (
                    <span className="flex items-center gap-1"><Award size={12} />{uni.scholarships.length} بورسیه</span>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => handleEdit(uni)} className="p-2 text-[#D4AF37] hover:bg-white/5 rounded-lg"><Edit2 size={18} /></button>
                  <button onClick={() => handleDelete(uni.id)} className="p-2 text-red-400 hover:bg-white/5 rounded-lg"><Trash2 size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
            <div className="glass-effect border border-white/10 rounded-2xl p-8 max-w-6xl w-full my-8" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-light text-white mb-6">{editingUniversity ? 'ویرایش دانشگاه' : 'دانشگاه جدید'}</h2>
              <form onSubmit={handleSubmit} className="space-y-6 max-h-[75vh] overflow-y-auto px-2">
                
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-zinc-300 mb-2">نام فارسی *</label>
                    <input type="text" name="name_fa" required value={formData.name_fa} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                  <div><label className="block text-sm text-zinc-300 mb-2">نام انگلیسی *</label>
                    <input type="text" name="name_en" required value={formData.name_en} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block text-sm text-zinc-300 mb-2">کشور *</label>
                    <select name="country_id" required value={formData.country_id} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none">
                      <option value="">انتخاب کنید</option>
                      {countries.map(c => <option key={c.id} value={c.id}>{c.name_fa}</option>)}
                    </select></div>
                  <div><label className="block text-sm text-zinc-300 mb-2">شهر *</label>
                    <input type="text" name="city_fa" required value={formData.city_fa} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                  <div><label className="block text-sm text-zinc-300 mb-2">رنکینگ</label>
                    <input type="text" name="rank" value={formData.rank} onChange={handleChange} placeholder="مثلاً: Top 100" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                </div>

                {/* Logos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-zinc-300 mb-2">لوگو دانشگاه</label>
                    {formData.logo_url && <img src={formData.logo_url} alt="Logo" className="w-32 h-32 object-cover rounded-lg mb-2" />}
                    <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer">
                      <Upload size={20} />{uploading ? 'در حال آپلود...' : 'آپلود لوگو'}
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo_url')} className="hidden" disabled={uploading} />
                    </label></div>
                  <div><label className="block text-sm text-zinc-300 mb-2">عکس دانشگاه</label>
                    {formData.image_url && <img src={formData.image_url} alt="Image" className="w-full h-32 object-cover rounded-lg mb-2" />}
                    <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer">
                      <Upload size={20} />{uploading ? 'در حال آپلود...' : 'آپلود عکس'}
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'image_url')} className="hidden" disabled={uploading} />
                    </label></div>
                </div>

                {/* Dormitories */}
                <div className="border border-white/10 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2"><Building2 size={20} className="text-[#D4AF37]" />خوابگاه‌ها</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={dormInput.name} onChange={(e) => setDormInput({ ...dormInput, name: e.target.value })} placeholder="نام خوابگاه" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={dormInput.address} onChange={(e) => setDormInput({ ...dormInput, address: e.target.value })} placeholder="آدرس" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={dormInput.monthly_cost} onChange={(e) => setDormInput({ ...dormInput, monthly_cost: e.target.value })} placeholder="هزینه ماهانه" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={dormInput.facilities} onChange={(e) => setDormInput({ ...dormInput, facilities: e.target.value })} placeholder="امکانات (با کاما)" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <button type="button" onClick={addDormitory} className="w-full px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] mb-3">افزودن خوابگاه</button>
                  <div className="space-y-2">
                    {formData.dormitories.map((dorm, idx) => (
                      <div key={`dorm-${dorm.name}-${idx}`} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg text-sm">
                        <div>
                          <p className="text-white font-medium">{dorm.name}</p>
                          <p className="text-zinc-400 text-xs">{dorm.address} • {dorm.monthly_cost}</p>
                        </div>
                        <button type="button" onClick={() => removeDormitory(idx)} className="text-red-400 hover:text-red-300"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scholarships */}
                <div className="border border-white/10 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2"><Award size={20} className="text-[#D4AF37]" />بورسیه‌ها</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={scholarshipInput.name} onChange={(e) => setScholarshipInput({ ...scholarshipInput, name: e.target.value })} placeholder="نام بورسیه" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={scholarshipInput.amount} onChange={(e) => setScholarshipInput({ ...scholarshipInput, amount: e.target.value })} placeholder="مبلغ" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={scholarshipInput.requirements} onChange={(e) => setScholarshipInput({ ...scholarshipInput, requirements: e.target.value })} placeholder="شرایط" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                    <input type="text" value={scholarshipInput.deadline} onChange={(e) => setScholarshipInput({ ...scholarshipInput, deadline: e.target.value })} placeholder="ددلاین" className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                  <button type="button" onClick={addScholarship} className="w-full px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] mb-3">افزودن بورسیه</button>
                  <div className="space-y-2">
                    {formData.scholarships.map((sch, idx) => (
                      <div key={`scholarship-${sch.name}-${idx}`} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg text-sm">
                        <div>
                          <p className="text-white font-medium">{sch.name} • {sch.amount}</p>
                          <p className="text-zinc-400 text-xs">{sch.requirements}</p>
                        </div>
                        <button type="button" onClick={() => removeScholarship(idx)} className="text-red-400 hover:text-red-300"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Facilities - امکانات */}
                <div className="border border-white/10 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Building2 size={20} className="text-[#D4AF37]" />
                    امکانات دانشگاه
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newFacility}
                      onChange={(e) => setNewFacility(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFacility())}
                      placeholder="مثلاً: کتابخانه، سالن ورزشی، آزمایشگاه..."
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={addFacility}
                      className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB]"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.facilities.map((facility, idx) => (
                      <div
                        key={`facility-${idx}`}
                        className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-lg text-sm"
                      >
                        <span className="text-white">{facility}</span>
                        <button
                          type="button"
                          onClick={() => removeFacility(idx)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {formData.facilities.length === 0 && (
                    <p className="text-zinc-500 text-sm mt-2">هنوز امکاناتی اضافه نشده است</p>
                  )}
                </div>

                <div className="flex gap-4">
                  <button type="submit" className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB]">{editingUniversity ? 'ذخیره تغییرات' : 'ایجاد دانشگاه'}</button>
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5">انصراف</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUniversities;